"use no memo";

import { CourseSchema } from "@repo/validators";
import { useTranslation } from "react-i18next";

import type { Course } from "~/providers/course-provider";
import { useOrderChartManager } from "~/managers/use-order-chart-manager";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import { ColorPicker } from "../ui/color-picker";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";
import { ModalInput } from "../ui/modal-input";

export default function OrderChartForm({
  orderChart,
  onClose,
}: {
  orderChart?: Course;
  onClose: (force?: boolean) => void;
}) {
  const { t } = useTranslation("common");

  const { createOrderChart, updateOrderChart } = useOrderChartManager();

  const { setUnsavedChanges } = useUnsavedChanges();

  const form = useForm({
    reValidateMode: "onChange",
    schema: CourseSchema,
    defaultValues: {
      name: orderChart?.name ?? "",
      color: orderChart?.color ?? "",
      position: orderChart?.position ?? 1,
    },
  });

  const isDirty = !!Object.values(form.formState.dirtyFields).length;

  setUnsavedChanges({
    isDirty,
    onDiscard() {
      form.reset();
      onClose(true);
    },
  });

  const handleSubmit = form.handleSubmit(async (formData) => {
    if (orderChart?.id) {
      const result = await updateOrderChart(orderChart.id, formData);
      if (result === true) {
        form.reset();
        onClose(true);
      }
    } else {
      const { data, error } = await createOrderChart(formData);
      if (data) {
        form.reset();
        onClose(true);
      } else if (error) {
        form.setError(error.path, { message: error.message });
      }
    }
  });

  return (
    <Form {...form}>
      <form
        id="order-chart-form"
        onSubmit={handleSubmit}
        className="grid grid-cols-2 gap-2"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("OrderChart.name")}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t("OrderChart.name")} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="position"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("OrderChart.position")}</FormLabel>
              <FormControl>
                <ModalInput type="number" min={1} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="color"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("OrderChart.color")}</FormLabel>
              <FormControl>
                <ColorPicker {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
